import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Descriptions, Modal, Row, Spin, Switch, Table, Tag, Typography, Divider, Badge } from 'antd';
import { SERVER_IP } from 'assets/Config';
import { API_STATUS, DATE_FORMAT } from 'constants/app-constants';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { capitalizeFirstLetter, getDateFormat, getINRMoneyFormat } from 'services/Utils';
import { useI18n } from 'hooks/useI18n';
import { putApi } from 'redux/sagas/putApiSaga';

const { Title, Text } = Typography;

const CustomerDetails = ({ id, onCloseCustomerDetails, viewDetails = false }) => {
	const customerRedux = useSelector((state) => state.customerRedux);
	const { t } = useI18n();
	const { selectedCustomerDetails } = customerRedux;
	const globalRedux = useSelector((state) => state.globalRedux);
	const dispatch = useDispatch();

	const columns = [
		{
			title: t('customers.payments.columns.number'),
			dataIndex: 'id',
			render: (text, row, index) => <Text>{index + 1}</Text>,
		},
		{
			title: t('customers.payments.columns.paid_on'),
			dataIndex: 'paidOn',
			render: (text) => <Text>{getDateFormat(text, DATE_FORMAT.DD_MM_YYYY)}</Text>,
		},
		{
			title: t('customers.payments.columns.paid_for'),
			dataIndex: 'membership',
			render: (text, record) => (
				<>
					{record.membership && (
						<Row gutter={[5, 5]}>
							<Col>
								<Tag color="blue">{t('customers.payments.labels.membership')}</Tag>
							</Col>
							<Col>
								<Tag color="cyan">{record.membership?.membershipName}</Tag>
							</Col>
						</Row>
					)}
					{record.service && (
						<Row gutter={[5, 5]}>
							<Col>
								<Tag color="blue">{t('customers.payments.labels.service')}</Tag>
							</Col>
							<Col>
								<Tag color="green">{record.service?.serviceName}</Tag>
							</Col>
						</Row>
					)}
					{record.product && (
						<Row gutter={[5, 5]}>
							<Col>
								<Tag color="purple">{t('customers.payments.labels.product')}</Tag>
							</Col>
							<Col>
								<Tag color="lime">{record.product?.productName}</Tag>
							</Col>
						</Row>
					)}
					{record.paymentFor === 'Personal Training' && (
						<Row gutter={[5, 5]}>
							<Col>
								<Tag color="volcano">{t('customers.payments.labels.personal_training')}</Tag>
							</Col>
						</Row>
					)}
					{!record.membership && !record.service && !record.product && record.paymentFor !== 'Personal Training' && (
						<Text>-</Text>
					)}
				</>
			),
		},
		{
			title: t('customers.payments.columns.paid'),
			dataIndex: 'paid',
			render: (text) => <Text>{getINRMoneyFormat(text)}</Text>,
		},
		{
			title: t('customers.payments.columns.balance'),
			dataIndex: 'balance',
			render: (text) => <Text>{getINRMoneyFormat(text)}</Text>,
		},
		{
			title: t('customers.payments.columns.method'),
			dataIndex: 'id',
			render: (text, record) => {
				return record.online ? (
					<Row gutter={[5, 5]}>
						<Col span={24}>
							<Tag color="blue">{t('customers.payments.labels.online')}</Tag>
						</Col>
						<Col>
							<Text type="secondary">{record.transactionId}</Text>
						</Col>
					</Row>
				) : (
					<Tag color="green">{t('customers.payments.labels.cash')}</Tag>
					);
			},
		},
		{
			title: t('customers.payments.columns.received_by'),
			dataIndex: 'coach',
			render: (coach) => <Text>{coach?.coachName}</Text>,
		},
	];	const getCustomerDetails = useCallback(() => {
		if (id) {
			let url = `${SERVER_IP}api/customers/${id}`;
			dispatch(getApi('SET_SELECTED_CUSTOMER_DETAILS', url));
		}
	}, [dispatch, id]);

	useEffect(() => {
		if (globalRedux.apiStatus.SET_SELECTED_CUSTOMER_DETAILS === 'SUCCESS') {
			dispatch(resetApiStatus('SET_SELECTED_CUSTOMER_DETAILS'));
		}
	}, [globalRedux.apiStatus.SET_SELECTED_CUSTOMER_DETAILS, dispatch]);

	useEffect(() => {
		getCustomerDetails();
	}, [getCustomerDetails]);

	const customerDetailsLoading = globalRedux.apiStatus.SET_SELECTED_CUSTOMER_DETAILS === API_STATUS.PENDING;

	const handleActivation = (isActive) => {
		let data = { isActive };
		let url = `${SERVER_IP}api/customers/changeStatus/${selectedCustomerDetails?.id}`;
		dispatch(putApi(data, 'CHANGE_STATUS', url));
	};

	useEffect(() => {
		if (globalRedux.apiStatus.CHANGE_STATUS === 'SUCCESS') {
			getCustomerDetails();
		}
	}, [globalRedux.apiStatus.CHANGE_STATUS, getCustomerDetails]);

	const changingStatus = globalRedux.apiStatus.CHANGE_STATUS === API_STATUS.PENDING;

	return (
		<Modal
			title={
				<Row align="middle" justify="space-between">
					<Col>
						<Title level={4}>{t('customers.customer_details')}</Title>
						{!customerDetailsLoading && (
							<Badge
								count={selectedCustomerDetails?.isActive ? t('common.active') : t('common.inactive')}
								style={{ backgroundColor: selectedCustomerDetails?.isActive ? '#52c41a' : '#ff4d4f' }}
							/>
						)}
					</Col>
					<Col>
						{!customerDetailsLoading && (
							<Switch
								checkedChildren={t('common.active')}
								unCheckedChildren={t('common.inactive')}
								checked={selectedCustomerDetails?.isActive}
								onChange={(value) => handleActivation(value)}
							/>
						)}
					</Col>
				</Row>
			}
			footer={
				<Button type="primary" onClick={onCloseCustomerDetails}>
					Close
				</Button>
			}
			width={'70%'}
			open={viewDetails}
			centered
			onCancel={onCloseCustomerDetails}>
			<Row>
				{customerDetailsLoading || changingStatus ? (
					<Col
						span={24}
						style={{
							height: 400,
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
						}}>
						<Row gutter={[10, 10]} justify="center" align={'middle'}>
							<Col span={24} style={{ textAlign: 'center' }}>
								<Spin size="large" />
							</Col>
							<Col span={24} style={{ textAlign: 'center' }}>
								<Text>{t('customers.fetching_details')}</Text>
							</Col>
						</Row>
					</Col>
				) : (
					<Col span={24} style={{ padding: 20, paddingTop: 0 }}>
						<Row style={{ marginBottom: 16 }}>
							{/* <Switch
								checkedChildren="Active"
								unCheckedChildren="Inactive"
								checked={selectedCustomerDetails?.isActive}
								onChange={(value) => handleActivation(value)}
							/> */}
						</Row>
						<Divider orientation="left">{t('customers.basic_info')}</Divider>
						<Descriptions bordered>
							<Descriptions.Item label={t('common.name')}>{selectedCustomerDetails?.customerName}</Descriptions.Item>
							<Descriptions.Item label={t('common.phone')}>{selectedCustomerDetails?.mobile}</Descriptions.Item>
							<Descriptions.Item label={t('common.email')}>{selectedCustomerDetails?.email}</Descriptions.Item>
							<Descriptions.Item label={t('customers.gender')}>
								{selectedCustomerDetails?.gender && capitalizeFirstLetter(selectedCustomerDetails?.gender)}
							</Descriptions.Item>
							<Descriptions.Item label={t('customers.date_of_birth')}>{getDateFormat(selectedCustomerDetails?.dob, DATE_FORMAT.DD_MM_YYYY)}</Descriptions.Item>
							<Descriptions.Item label={t('customers.age')}>{selectedCustomerDetails?.age}</Descriptions.Item>
							<Descriptions.Item label={t('customers.height')}>{selectedCustomerDetails?.height}</Descriptions.Item>
							<Descriptions.Item label={t('customers.weight')}>{selectedCustomerDetails?.weight}</Descriptions.Item>
							<Descriptions.Item label={t('customers.occupation')}>{selectedCustomerDetails?.occupation}</Descriptions.Item>
							{selectedCustomerDetails?.referral && (
								<Descriptions.Item label={t('customers.referred_by')}>{selectedCustomerDetails?.referral?.customerName}</Descriptions.Item>
							)}
							<Descriptions.Item label={t('common.address')}>{selectedCustomerDetails?.address}</Descriptions.Item>
						</Descriptions>
						<Divider orientation="left">{t('customers.payments.title')}</Divider>
						{selectedCustomerDetails?.payments?.length > 0 ? (
							<Table columns={columns} dataSource={selectedCustomerDetails?.payments} pagination={{ pageSize: 5 }} />
						) : (
							<Text>{t('customers.no_payments')}</Text>
						)}
					</Col>
				)}
			</Row>
		</Modal>
	);
};

export default CustomerDetails;
